import React, { useState, Suspense, lazy } from 'react';
import { getApiUrl } from './lib/getApiUrl';
// CB:BEGIN responsive-css-import
import './styles/responsive.css';
// CB:END responsive-css-import
import ResponsiveContainer from './components/layout/ResponsiveContainer';
import LandingPage from './components/LandingPage';
import MobileLanding from './components/MobileLanding';
// CB:BEGIN contractor-signup-import
import ContractorSignup from './components/ContractorSignup.tsx';
// CB:END contractor-signup-import
// CB:BEGIN invite-preview-import
import InvitePreview from './components/InvitePreview';
// CB:END invite-preview-import
import ContractorDashboard from './components/ContractorDashboard';
import EstimateForm from './components/EstimateForm';
import TwilioOptInPage from './components/TwilioOptInPage';
import PaymentScreen from './components/PaymentScreen';
import ProjectDetails from './components/ProjectDetails';
import ProjectWizard from './components/ProjectWizard';

import ContractorBids from './components/ContractorBids';
import ContractorDetails from './components/ContractorDetails';
import CurrentBids from './components/CurrentBids';
import ContractorActiveProjects from './components/ContractorActiveProjects';
import ContractorLandingPage from './components/ContractorLandingPage';
// CB:BEGIN fr-014-realtor-imports
import RealtorLanding from './pages/RealtorLanding';
import RealtorDashboard from './pages/RealtorDashboard';
import RealtorQuoteDetail from './pages/RealtorQuoteDetail';
import RealtorClientPortal from './pages/RealtorClientPortal';
// CB:END fr-014-realtor-imports
import Waitlist from './components/Waitlist';
import FAQ from './components/FAQ';
import Terms from './components/Terms';
import PaymentProtectionTerms from './components/PaymentProtectionTerms'; // CB:BEGIN payment-protection-terms
import HomeownerProjectAgreement from './components/HomeownerProjectAgreement'; // CB:BEGIN homeowner-project-agreement
import ContractorBidAgreement from './components/ContractorBidAgreement'; // CB:BEGIN contractor-bid-agreement
import AboutCoreBids from './components/AboutCoreBids';
import ContractorVerification from './components/ContractorVerification';
import ContractorOnboarding from './components/ContractorOnboarding';
import ContractorPayments from './components/ContractorPayments';
import NotFound from './components/NotFound';
import AdminTools from './components/AdminTools';
import PasswordPrompt from './components/PasswordPrompt';
// CB:BEGIN clerk-diagnostics-import
import ClerkDiagnostics from './pages/ClerkDiagnostics';
// CB:END clerk-diagnostics-import
// FIX: Wrap contractor routes in error boundary for safer dev experience
import { ErrorBoundary } from './components/ErrorBoundary';
import AdminHomeownersList from './pages/admin/AdminHomeownersList';
import AdminHomeownerDetail from './pages/admin/AdminHomeownerDetail';
import AdminAddHomeowner from './pages/admin/AdminAddHomeowner';
import AdminEditHomeownerDashboard from './pages/admin/AdminEditHomeownerDashboard';
import AdminContractorsList from './pages/admin/AdminContractorsList';
import AdminAddContractor from './pages/admin/AdminAddContractor';
import AdminEditContractorDashboard from './pages/admin/AdminEditContractorDashboard';
import AdminProjectsList from './pages/admin/AdminProjectsList';
import AdminSettings from './pages/admin/AdminSettings';
import AdminCreate from './pages/AdminCreate';
// CB:BEGIN fr-034-admin-realtors-import
import AdminRealtorsPage from './pages/admin/AdminRealtorsPage';
// CB:END fr-034-admin-realtors-import
import AcceptTermsPage from './pages/AcceptTermsPage';
import HomeownerDashboard from './pages/HomeownerDashboard';
import HomeownerProjectPage from './pages/HomeownerProjectPage';
import HomeownerDocumentsPage from './pages/HomeownerDocumentsPage';
// CB:BEGIN role-help-pages-import
import HomeownerHelpPage from './pages/help/HomeownerHelpPage';
import ContractorHelpPage from './pages/help/ContractorHelpPage';
// CB:END role-help-pages-import
// CB:BEGIN admin-dashboard-import
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminLayout from './components/admin/AdminLayout';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import useMetaPixel from './hooks/useMetaPixel';
import useAnalytics from './hooks/useAnalytics';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminUserEditPage from './pages/admin/AdminUserEditPage';
import AdminProjectsPage from './pages/admin/AdminProjectsPage';
import AdminProjectDetailPage from './pages/admin/AdminProjectDetailPage';
import ProjectCockpit from './pages/admin/ProjectCockpit';
// CB:BEGIN admin-reports-page-import
import AdminReportsPage from './pages/admin/AdminReportsPage';
// CB:END admin-reports-page-import
// CB:BEGIN admin-audit-logs-page-import
import AdminAuditLogsPage from './pages/admin/AdminAuditLogsPage';
// CB:END admin-audit-logs-page-import
// CB:END admin-dashboard-import
// CB:BEGIN admin-imports
// CB:END admin-imports
import AdminContractorDetail from './pages/admin/AdminContractorDetail';
import ServicedZips from './components/ServicedZips';
import Settings from './pages/Settings';

// FIX: Enforce policy with UX (toast/banner) instead of silent no-op
import { gateContractorRoute } from './auth/ensureHomeowner';
import { toast } from 'sonner'; // or your toast lib

// CB:BEGIN lazy-imports
// Lazy load heavy screens for better performance
const CustomerDashboard = lazy(() => import('./components/CustomerDashboard'));
const EscrowDashboard = lazy(() => import('./components/EscrowDashboard'));
const ContractorEscrowDashboard = lazy(() => import('./components/ContractorEscrowDashboard'));
const ContractorLicenseVerification = lazy(() => import('./components/ContractorLicenseVerification'));
const ContractorInsuranceUpload = lazy(() => import('./components/ContractorInsuranceUpload'));
const EnhancifyFinancing = lazy(() => import('./components/EnhancifyFinancing'));
const NextSteps = lazy(() => import('./components/NextSteps'));
const ProjectMilestonePage = lazy(() => import('./pages/ProjectMilestonePage'));
const ContractorMilestonePage = lazy(() => import('./pages/ContractorMilestonePage'));

// Fallback component for lazy loading
function ScreenFallback() {
  return (
    <div style={{ 
      padding: 24, 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      minHeight: '200px',
      fontSize: '16px',
      color: '#6b7280'
    }}>
      Loading...
    </div>
  );
}
// CB:END lazy-imports
import { GlobalMergeService } from './lib/globalMergeService';
import AuthRedirectWrapper from './components/AuthRedirectWrapper';
import { useUser, useAuth, useClerk } from '@clerk/clerk-react';
import { showDevPane as envShowDevPane } from './config/env';
import { MobileProvider } from './contexts/MobileContext';
import { ProfileProvider, useProfile } from './context/ProfileContext';
import { disableDemoMode } from './lib/featureFlags';
import Report from './components/Report';
import PrivacyPolicy from './components/PrivacyPolicy';
import AccessibilityStatement from './components/AccessibilityStatement';
import HomeownerReportPage from './components/HomeownerReportPage';
import DataTracking from './components/DataTracking';
// CB:BEGIN lazy-load-kpi-tracker
// Lazy load KPITracker to avoid TDZ issues with admin/KPI code
const KPITracker = lazy(() => import('./components/admin/KPITracker'));
// CB:END lazy-load-kpi-tracker
import EagleViewSandbox from './components/EagleViewSandbox';
import EmailReportOrdered from './components/emails/EmailReportOrdered';
import EmailReportReady from './components/emails/EmailReportReady';
import EmailBidAccepted from './components/emails/EmailBidAccepted';
import EmailStartDateSet from './components/emails/EmailStartDateSet';
import EmailStartReminder from './components/emails/EmailStartReminder';
import EmailOutOfScope from './components/emails/EmailOutOfScope';
import StripeConnectDemo from './pages/demo/StripeConnectDemo';
import PaymentsEscrowDemo from './pages/demo/PaymentsEscrowDemo';
import { useProgressiveDataService, getTempSessionId, clearTempSessionId } from './lib/progressiveDataService';
import { useUrlSync } from './hooks/useUrlSync';
import {
  consumePostAuthRedirect,
  hasRedirectLock,
  isOnboardingPath,
  clearClerkRedirectParams,
} from './auth/postAuth';
import { getScreenFromUrl, isPublicRoute } from './lib/routes';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import DevHud from './components/DevHud';

// CB:BEGIN fix-migration-log-spam
// Module-level Set to track logged migrations (persists across renders)
const loggedMigrationSet = new Set<string>();
// CB:END fix-migration-log-spam

// CB:BEGIN sign-in-modal-launcher
/**
 * Renders the landing page and immediately opens the Clerk sign-in modal on top.
 * Used when the user navigates directly to /sign-in so they get the popup
 * experience instead of a full-page form.
 */
function SignInModalLauncher({
  onNavigate,
  openSignIn,
}: {
  onNavigate: (screen: string) => void;
  openSignIn: (opts?: object) => void;
}) {
  const POST_AUTH_KEY = 'postAuth:redirect';

  React.useEffect(() => {
    const postAuth = sessionStorage.getItem(POST_AUTH_KEY);
    const redirectUrl = postAuth && postAuth.startsWith('/') ? postAuth : '/';
    openSignIn({
      fallbackRedirectUrl: redirectUrl,
      redirectUrl: undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render the landing page behind the modal so the backdrop isn't blank
  return <LandingPage onNavigate={onNavigate} />;
}
// CB:END sign-in-modal-launcher

function App() {
  const [currentScreen, setCurrentScreen] = useState('landing'); // Will be updated by useEffect
  const [devNavCollapsed, setDevNavCollapsed] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const { user, isSignedIn } = useUser();
  const { isLoaded, getToken } = useAuth();
  // CB:BEGIN qa-role-check
  // In QA/demo mode, check if a QA session is active via the backend test helper.
  // This lets Playwright E2E tests navigate authenticated pages without a real Clerk session.
  const [qaRole, setQaRole] = React.useState<string | null>(null);
  const [qaRoleChecked, setQaRoleChecked] = React.useState(!import.meta.env.VITE_DEMO_MODE);
  React.useEffect(() => {
    if (!import.meta.env.VITE_DEMO_MODE || import.meta.env.PROD) { setQaRoleChecked(true); return; }
    fetch('/api/_test/status', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.qaUser) setQaRole(data.role || null); })
      .catch(() => { /* endpoint optional — 404 is safe to ignore */ })
      .finally(() => setQaRoleChecked(true));
  }, []);
  // CB:END qa-role-check
  const { openSignIn } = useClerk();
  const { processSignup } = useProgressiveDataService();
  const { requiresLegalAcceptance, refreshProfile } = useProfile();

  // CB:BEGIN map-admin-path-initial
  // CB:BEGIN handle-financing-route-initial
  // Handle /financing route immediately on page load (before Clerk loads)
  React.useEffect(() => {
    try {
      const path = window.location.pathname || '';
      const isAdminPath = /^\/admin(\/|$)/i.test(path);
      const demo = import.meta.env?.VITE_DEMO_MODE === 'true' || import.meta.env?.VITE_ADMIN_TEST_BYPASS === 'true';
      if (isAdminPath && demo) {
        setCurrentScreen('admin-dashboard');
        return;
      }
      
      // Handle /financing route immediately
      if (path === '/financing') {
        const screenFromUrl = getScreenFromUrl(path);
        if (screenFromUrl) {
          console.log('✅ App: Setting financing screen on initial load:', path, '→', screenFromUrl);
          setCurrentScreen(screenFromUrl);
          return;
        }
      }
    } catch {}
  }, []);
  // CB:END handle-financing-route-initial
  // CB:END map-admin-path-initial

  // CB:BEGIN provision-then-merge
  // Ensure User + role profile exist (idempotent), then run global merge when user signs up
  React.useEffect(() => {
    const run = async () => {
      if (!isSignedIn || !user) return;
      try {
        const token = await getToken();
        if (token) {
          const provisionRes = await fetch(getApiUrl('/api/auth/provision'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            credentials: 'include',
          });
          if (provisionRes.ok) {
            console.log('✅ App: Provision completed');
          }
        }
      } catch (_) {}
      console.log('🌐 App: User signed in, attempting global merge');
      const mergeSuccess = await GlobalMergeService.attemptMerge(user);
      if (mergeSuccess) {
        console.log('✅ App: Global merge completed successfully');
      } else {
        console.log('⚠️ App: Global merge skipped or failed');
      }
    };
    run();
  }, [isSignedIn, user, getToken]);
  // CB:END provision-then-merge

  // CB:BEGIN dev-whoami-ping
  // Dev-only: ping canonical backend to confirm proxy/backend is correct (never log token)
  React.useEffect(() => {
    if (!import.meta.env.DEV) return;
    let cancelled = false;
    fetch(getApiUrl('/api/debug/whoami'), { credentials: 'include' })
      .then((res) => (cancelled ? null : res.json().catch(() => ({}))))
      .then((data) => {
        if (cancelled || !data) return;
        const server = data?.serverName ?? data?.server ?? 'unknown';
        const derived = data?.derived ?? {};
        console.log('[dev] backend whoami:', server, { userId: derived?.userId ? 'present' : 'none', role: derived?.role, ts: data?.ts });
        if (server !== 'canonical-backend') {
          console.warn('[dev] Backend may not be canonical. Start with: npm run dev:backend');
        }
      })
      .catch(() => {
        if (!cancelled) console.warn('[dev] Backend unreachable at /api/debug/whoami. Start backend: npm run dev:backend');
      });
    return () => { cancelled = true; };
  }, []);
  // CB:END dev-whoami-ping

  // CB:BEGIN auto-route-mobile-landing
  // Check for mobile on initial load and whenever screen changes to landing
  React.useEffect(() => {
    const checkMobile = () => {
      try {
        const ua = navigator.userAgent || '';
        const isUaMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|BB10|IEMobile|Opera Mini|KaiOS/i.test(ua);
        const hasTouch = ('ontouchstart' in window) || (navigator as any)?.maxTouchPoints > 0;
        const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
        // Match useDevice hook logic: w <= 900 for mobile layout
        const isSmall = window.innerWidth <= 900;
        const preferMobile = !!(isUaMobile && (hasTouch || coarse) && isSmall);
        
        // Always check on landing screen, don't rely on sessionStorage
        if (preferMobile && currentScreen === 'landing') {
          setCurrentScreen('mobile-landing');
        }
      } catch {}
    };
    
    // Check immediately
    checkMobile();
    
    // Also check on resize/orientation change
    const handleResize = () => {
      if (currentScreen === 'landing' || currentScreen === 'mobile-landing') {
        checkMobile();
      }
    };
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [currentScreen]);
  // CB:END auto-route-mobile-landing

  // CB:BEGIN debug-force-mobile-landing
  React.useEffect(() => {
    try {
      const qp = new URLSearchParams(window.location.search);
      if (qp.get('cb_preview_mobile') === '1') {
        console.log('✅ Forcing mobile landing preview via ?cb_preview_mobile=1');
        setCurrentScreen('mobile-landing');
      }
    } catch {}
  }, []);
  // CB:END debug-force-mobile-landing

  // Global: if user just signed in and there is a temp session, migrate temp -> homeowner
  React.useEffect(() => {
    const migrateIfNeeded = async () => {
      try {
        if (!isSignedIn || !user) return;
        // Backend role check: if DB says CONTRACTOR, never migrate to homeowner
        // CB:BEGIN remove-get-usertype-call
        // Use /api/me for role detection; remove legacy /api/get-usertype to avoid 404s
        // CB:BEGIN fix-401-on-api-me
        // Only call /api/me if user is signed in and has a token
        try {
          if (!isSignedIn || !user) return;
          const token = await user.getToken();
          if (!token) return;
          
          const meRes = await fetch(getApiUrl('/api/me'), {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            credentials: 'include'
          });
          if (meRes.ok) {
            const meJson = await meRes.json();
            const role = String(meJson?.role || '').toUpperCase();
            if (role === 'CONTRACTOR') return; // do not auto-migrate contractors
          } else if (meRes.status === 401) {
            // User not authenticated - skip silently
            return;
          }
        } catch (err) {
          // Silently ignore errors - this is a non-critical check
        }
        // CB:END fix-401-on-api-me
        // CB:END remove-get-usertype-call
        // CB:BEGIN skip-homeowner-migration-for-contractors
        try {
          const meta = (user.publicMetadata as any) || {};
          const usertype = (meta.usertype || meta.userType || '').toString().toLowerCase();
          const path = window.location.pathname;
          const isOnContractorFlow = /^(\/contractor|\/current-bids|\/contractor-dashboard|\/contractor-verification|\/contractor-start)/i.test(path);
          if (usertype === 'contractor' || isOnContractorFlow) {
            return; // do not auto-migrate contractors to homeowner
          }
        } catch {}
        // CB:END skip-homeowner-migration-for-contractors
        
        // Skip migration for admin users (no homeowner flow needed)
        try {
          const meta = (user.publicMetadata as any) || {};
          const usertype = (meta.usertype || meta.userType || '').toString().toLowerCase();
          if (usertype === 'admin') return;
        } catch {}

        // Check if migration has already been attempted for this user
        const migrationKey = `migration_attempted_${user.id}`;
        // CB:BEGIN fix-migration-log-spam
        // Only log once per session using module-level Set
        if (localStorage.getItem(migrationKey)) {
          if (!loggedMigrationSet.has(user.id)) {
            console.log('🔄 Migration already attempted for user:', user.id);
            loggedMigrationSet.add(user.id);
          }
          return;
        }
        // CB:END fix-migration-log-spam
        
        const tempSessionId = (typeof getTempSessionId === 'function'
          ? getTempSessionId()
          : null) || localStorage.getItem('temp_session_id');

        if (!tempSessionId) return;

        console.log('🔄 Attempting migration for user:', user.id);
        await processSignup({
          user_id: user.id,
          email: user.emailAddresses?.[0]?.emailAddress || '',
          account_type: 'HOMEOWNER',
          temp_session_id: tempSessionId,
        });
        
        // Mark migration as attempted
        localStorage.setItem(migrationKey, 'true');
        
        if (typeof clearTempSessionId === 'function') {
          clearTempSessionId();
        } else {
          localStorage.removeItem('temp_session_id');
        }
      } catch (error) {
        console.error('❌ Migration failed:', error);
      }
    };
    migrateIfNeeded();
  }, [isSignedIn, user, processSignup]);

  // CB:BEGIN clerk-aware-initial-screen
  // Wait for Clerk to be loaded before determining initial screen
  React.useEffect(() => {
    if (!isLoaded) return; // wait for Clerk to be ready
    if (!qaRoleChecked) return; // wait for QA session check (demo mode only)
    
    const path = window.location.pathname;
    
    // Always allow /next-steps to render, even pre-auth
    if (path === '/next-steps') {
      setCurrentScreen('roof-report-coming-soon');
      return;
    }
    
    // CB:BEGIN handle-financing-route
    // Handle /financing route - check if it should be public or require auth
    if (path === '/financing') {
      const screenFromUrl = getScreenFromUrl(path);
      if (screenFromUrl) {
        console.log('✅ App: Setting financing screen from URL:', path, '→', screenFromUrl);
        setCurrentScreen(screenFromUrl);
        return;
      }
    }
    // CB:END handle-financing-route
    
    // CB:BEGIN allow-public-routes-pre-auth
    // Check if this is a public route (like /signup or /invite/:token) that should be accessible without auth
    if (!isSignedIn) {
      if (path === '/sign-in' || path.startsWith('/sign-in?')) {
        setCurrentScreen('sign-in');
        return;
      }
      const screenFromUrl = getScreenFromUrl(path);
      const isPublic = isPublicRoute(path);

      // CB:BEGIN handle-invite-route-explicitly
      // Explicitly handle /invite/:token route (must be public)
      if (path.match(/^\/invite\/[^/]+/)) {
        console.log('✅ App: Allowing invite route without auth:', path);
        setCurrentScreen('invite-preview');
        return;
      }
      // CB:END handle-invite-route-explicitly
      // CB:BEGIN fr-014-realtor-portal-public
      if (path.match(/^\/realtor\/portal\/[^/]+/)) {
        setCurrentScreen('realtor-client-portal');
        return;
      }
      // CB:END fr-014-realtor-portal-public
      if (isPublic && screenFromUrl) {
        console.log('✅ App: Allowing public route without auth:', path, '→', screenFromUrl);
        setCurrentScreen(screenFromUrl);
        return;
      }
      // CB:BEGIN qa-bypass-url-routing
      // QA mode: if _qa_user cookie is set (non-httpOnly), allow URL-based routing without Clerk session
      const isQaSession = import.meta.env.VITE_DEMO_MODE === 'true' &&
        typeof document !== 'undefined' &&
        document.cookie.split(';').some(c => c.trim().startsWith('_qa_user='));
      if (isQaSession && screenFromUrl) {
        console.log('✅ App: QA bypass routing:', path, '→', screenFromUrl);
        setCurrentScreen(screenFromUrl);
        return;
      }
      // CB:END qa-bypass-url-routing
    }
    // CB:END allow-public-routes-pre-auth
    
    // Honor post-auth redirect if present
    const post = consumePostAuthRedirect();
    if (post && isSignedIn) {
      setCurrentScreen(post === '/dashboard' ? 'customer-dashboard' : 'roof-report-coming-soon');
      return;
    }
    
    // Default routing based on auth state
    if (isSignedIn) {
      // CB:BEGIN preserve-user-navigation-intent
      // CB:BEGIN role-based-dashboard-redirect
      // CB:BEGIN prefer-clerk-role-over-cache
      // Check Clerk metadata first, then fallback to cache
      const meta = (user?.publicMetadata as any) || {};
      let usertype = (meta.usertype || meta.userType || '').toString().toLowerCase();
      const cachedUserType = localStorage.getItem('cb.usertype');
      
      // If Clerk says one thing but cache says another, prefer Clerk and fix cache
      if (usertype && cachedUserType && usertype !== cachedUserType) {
        console.log('⚠️ App: Role mismatch - Clerk says', usertype, 'but cache says', cachedUserType, '- preferring Clerk');
        try { localStorage.setItem('cb.usertype', usertype); } catch {}
      } else if (!usertype && cachedUserType) {
        // If no Clerk role but we have cache, use cache for now
        usertype = cachedUserType;
      }
      // CB:END prefer-clerk-role-over-cache
      
      console.log('🔍 App: Cached usertype:', cachedUserType, 'Clerk usertype:', usertype);
      
      console.log('🔍 App: Signed-in user, current path:', path);
      // CB:BEGIN handle-admin-path-on-signin
      // Preserve admin sub-routes (e.g. /admin/quotes/new → admin-quotes-new) instead of always forcing dashboard
      if (usertype === 'admin' && (path === '/admin' || path.startsWith('/admin/'))) {
        const screenFromUrl = getScreenFromUrl(path);
        const target = screenFromUrl || 'admin-dashboard';
        console.log('🔍 App: Admin user on admin path, setting screen from URL:', path, '→', target);
        setCurrentScreen(target);
        return;
      }
      // CB:END handle-admin-path-on-signin
      if (path === '/' || path === '/landing') {
        // If no role from Clerk or cache, default homeowners on first landing to homeowner
        if (!usertype) {
          try { localStorage.setItem('cb.usertype', 'homeowner'); } catch {}
          usertype = 'homeowner';
        }
        // Redirect based on user type - prefer Clerk role over cache (with homeowner default)
        if (usertype === 'contractor') {
          console.log('🔄 App: Redirecting contractor to contractor-dashboard');
          setCurrentScreen('contractor-dashboard');
        } else if (usertype === 'realtor') {
          console.log('🔄 App: Redirecting realtor to realtor-dashboard');
          setCurrentScreen('realtor-dashboard');
        } else if (usertype === 'admin') {
          console.log('🔍 App: Redirecting admin to admin-dashboard');
          setCurrentScreen('admin-dashboard');
        } else {
          console.log('🔍 App: Redirecting to customer dashboard from landing page');
          setCurrentScreen('customer-dashboard');
        }
      } else if (path === '/next-steps') {
        console.log('🔍 App: Staying on next-steps page');
        setCurrentScreen('roof-report-coming-soon');
      } else if (path === '/financing') {
        // Handle financing route for signed-in users
        const screenFromUrl = getScreenFromUrl(path);
        if (screenFromUrl) {
          console.log('✅ App: Setting financing screen for signed-in user:', path, '→', screenFromUrl);
          setCurrentScreen(screenFromUrl);
        }
      } else {
        // CB:BEGIN handle-other-routes-for-signed-in
        // Check if path maps to a valid screen
        const screenFromUrl = getScreenFromUrl(path);
        if (screenFromUrl) {
          console.log('✅ App: Setting screen from URL for signed-in user:', path, '→', screenFromUrl);
          setCurrentScreen(screenFromUrl);
        } else {
          console.log('🔍 App: Not auto-redirecting, staying on current path:', path);
        }
        // CB:END handle-other-routes-for-signed-in
      }
      // CB:END role-based-dashboard-redirect
    } else {
      // CB:BEGIN qa-demo-url-routing
      // In demo/QA mode: if the URL maps to a specific screen, optimistically render it.
      // Components will handle 401 (redirect to landing) if there's no real/QA session.
      const screenFromUrl = getScreenFromUrl(path);
      if (import.meta.env.VITE_DEMO_MODE === 'true' && screenFromUrl && path !== '/' && path !== '/landing') {
        console.log('✅ App: Demo mode url routing (no clerk):', path, '→', screenFromUrl);
        setCurrentScreen(screenFromUrl);
      } else {
        setCurrentScreen('landing');
      }
      // CB:END qa-demo-url-routing
    }
  }, [isLoaded, isSignedIn, qaRoleChecked]);
  // CB:END clerk-aware-initial-screen

  // Redirect to accept-terms when backend requires legal acceptance (403 REQUIRES_ACCEPTANCE)
  React.useEffect(() => {
    if (!isSignedIn || !requiresLegalAcceptance || currentScreen === 'accept-terms') return;
    setCurrentScreen('accept-terms');
    if (window.location.pathname !== '/accept-terms') {
      window.history.pushState({ screen: 'accept-terms' }, '', '/accept-terms');
    }
  }, [isSignedIn, requiresLegalAcceptance, currentScreen]);

  // Consolidated redirect logic - single effect that respects redirect lock
  React.useEffect(() => {
    if (!isSignedIn || !user) return;

    // Never hijack while an interactive flow is in progress
    if ((window as any).__CB_BLOCK_AUTO_NAV) return;
    // If we've already gotten to the payment screen, leave it alone
    if (currentScreen === 'payment-screen') return;

    // If we're already on an onboarding page, do nothing.
    if (isOnboardingPath(window.location.pathname)) return;

    // Hard block: if a page explicitly set a no-redirect flag (e.g., payment screen), skip all auto-redirects
    try { if ((window as any).__CB_BLOCK_AUTO_NAV) return; } catch {}
    const path = window.location.pathname || '';
      if (/\/payment(-screen)?$/i.test(path)) return;

    // If the app previously saved a target, consume it and go there.
    // CB:BEGIN suppress-interactive-redirects
    const pathNow = window.location.pathname;
    const interactiveSuppress = [
      '/contractor-bids',
      '/payment',
      '/payment-screen',
      '/project-wizard',
      '/estimate',
      '/roof-report-coming-soon',
      '/next-steps',
      '/admin/quotes/new',
      '/admin/quotes/drafts',
      '/admin/invoices/create'
    ];
    if (interactiveSuppress.some(p => pathNow.startsWith(p))) {
      try { sessionStorage.removeItem('postAuth:redirect'); } catch {}
      return;
    }
    const saved = consumePostAuthRedirect();
    // CB:END suppress-interactive-redirects
    if (saved) {
      clearClerkRedirectParams();
      // If user is contractor OR we're on contractor flow, never honor a saved '/dashboard' redirect
      try {
        const meta = (user.publicMetadata as any) || {};
        const usertype = (meta.usertype || meta.userType || '').toString().toLowerCase();
        const path = window.location.pathname;
        const isOnContractorFlow = /^(\/contractor|\/current-bids|\/contractor-dashboard|\/contractor-verification|\/contractor-start)/i.test(path);
        const isOnAdminFlow = /^(\/admin)/i.test(path);
        
        if ((usertype === 'contractor' || isOnContractorFlow) && saved === '/dashboard') {
          console.log('🎯 App: Ignoring homeowner redirect for contractor, routing to contractor-dashboard');
          setCurrentScreen('contractor-dashboard');
          return;
        }
        
        // CB:BEGIN preserve-admin-subroute
        // Don't overwrite admin sub-screens (e.g. admin-quotes-new); URL may not have updated yet
        if (usertype === 'admin' && !isOnAdminFlow && !currentScreen?.startsWith('admin-')) {
          console.log('🎯 App: Redirecting admin user to admin-dashboard');
          setCurrentScreen('admin-dashboard');
          return;
        }
        // CB:END preserve-admin-subroute
      } catch {}

      const screenMap: Record<string, string> = {
        '/dashboard': 'customer-dashboard',
        '/contractor-dashboard': 'contractor-dashboard',
        '/builder-dashboard': 'builder-dashboard',
        '/measurement': 'roof-report-coming-soon',
        '/next-steps': 'roof-report-coming-soon',
      };
      const screen = screenMap[saved] || 'roof-report-coming-soon';
      console.log('🎯 App: Using consumed post-auth redirect:', saved, '→', screen);
      setCurrentScreen(screen);
      return;
    }

    // If a lock is active (we navigated to Next Steps but the page re-mounted)
    // don't perform any "auto redirects".
    if (hasRedirectLock()) {
      console.debug('App: redirect lock active, suppressing auto-redirects');
      return;
    }

    // Optional: only here you may keep a Clerk-provided redirect
    // (but *only* if there is no saved target and no lock)
    // const clerkRedirect = new URLSearchParams(window.location.search).get("redirect_url");
    // if (clerkRedirect) navigate(clerkRedirect, { replace: true });

  }, [isSignedIn, user, currentScreen]);


  // Handle Clerk redirects and convert to custom navigation
  React.useEffect(() => {
    const handleClerkRedirect = () => {
      let path = window.location.pathname || '';
      // Normalize known path quirks (e.g. /adminlocal -> /admin) for clearer logs
      if (path === '/adminlocal' || path === '/adminlocal/') path = '/admin';
      const publicRoutes = ['/', '/waitlist', '/faq', '/accept-terms', '/terms', '/payment-terms', '/homeowner-agreement', '/contractor-agreement', '/privacy-policy', '/accessibility-statement', '/reports'];
      if (!publicRoutes.includes(path) && import.meta.env.DEV) {
        console.log('🔍 Clerk redirect detected:', path);
      }
    };

    // Check for Clerk redirects on mount
    handleClerkRedirect();

    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', handleClerkRedirect);

    return () => {
      window.removeEventListener('popstate', handleClerkRedirect);
    };
  }, []);

  // Dev nav visibility summary (logged once, dev only)
  if (import.meta.env.DEV && envShowDevPane && !(window as { __devNavLogged?: boolean }).__devNavLogged) {
    console.log('Dev Nav Summary:', { viteShowDevNav: true, visible: true });
    (window as { __devNavLogged?: boolean }).__devNavLogged = true;
  }

  const handleNavigate = async (screen: string) => {
    // FIX: Handle contractor milestones route
    if (typeof screen === "string" && screen.includes("/contractor/projects/") && screen.includes("/milestones")) {
      if (import.meta.env.DEV) console.log('🚦 handleNavigate contractor milestones route:', { input: screen, target: 'contractor-project-milestones' });
      const projectId = screen.replace('/contractor/projects/', '').split('/')[0];
      if (projectId && window.location.pathname !== screen) {
        window.history.pushState({ screen: 'contractor-project-milestones', projectId }, '', screen);
      }
      setCurrentScreen('contractor-project-milestones');
      return;
    }
    
    // FIX: Preserve dynamic contractor details route and remove old alias
    if (typeof screen === "string" && screen.startsWith("/contractor/projects/")) {
      if (import.meta.env.DEV) console.log('🚦 handleNavigate contractor project route:', { input: screen, target: 'contractor-project-details' });
      // Update URL immediately to preserve project ID
      const projectId = screen.replace('/contractor/projects/', '').split('/')[0];
      if (projectId && window.location.pathname !== screen) {
        window.history.pushState({ screen: 'contractor-project-details' }, '', screen);
      }
      setCurrentScreen('contractor-project-details');
      return;
    }
    // CB:BEGIN fr-014-realtor-quote-detail-navigate
    if (typeof screen === "string" && screen.startsWith("/realtor/quotes/")) {
      const quoteId = screen.replace('/realtor/quotes/', '').split('/')[0];
      if (quoteId && window.location.pathname !== screen) {
        window.history.pushState({ screen: 'realtor-quote-detail', quoteId }, '', screen);
      }
      setCurrentScreen('realtor-quote-detail');
      return;
    }
    // CB:END fr-014-realtor-quote-detail-navigate
    
    // FIX: Remove/replace old alias - require id or send to current-bids
    if (screen === "project-details") {
      console.warn("normalize: old alias called without id; sending to current-bids");
      setCurrentScreen('current-bids');
      return;
    }
    
    // Normalize screen name: strip query/hash and map slugs → screen keys
    let target = screen || '';
    try {
      if (target.startsWith('/')) {
        const maybe = getScreenFromUrl(target);
        if (maybe) target = maybe;
        target = target.replace(/^\//, '');
      }
      const q = target.indexOf('?');
      const h = target.indexOf('#');
      const cut = [q, h].filter(i => i >= 0).sort((a,b) => a-b)[0];
      if (cut >= 0) target = target.slice(0, cut);
    } catch {}
    if (!target) target = 'landing';
    if (import.meta.env.DEV) console.log('🚦 handleNavigate normalized:', { input: screen, target });

    // Handle special routing for reports
    if (target.startsWith('reports/')) {
      setCurrentScreen('reports');
      return;
    }

    // CB:BEGIN unified-role-gate
    try {
      // Use UserTypeService for more robust usertype detection
      const meta = (user?.publicMetadata as any) || {};
      let usertype = (meta.usertype || meta.userType || '').toString().toLowerCase();
      
      // Debug logging will be moved after normalizedTarget is defined
      
      // If usertype is not found in metadata, check localStorage cache
        const cached = localStorage.getItem("cb.usertype");
      if (!usertype || usertype === '') {
        if (import.meta.env.DEV) console.log('🔍 Checking localStorage cache:', cached);
        if (cached === "contractor" || cached === "homeowner") {
          usertype = cached;
        }
      } else if (cached && cached !== usertype) {
        // Clerk metadata exists but differs from cache - prefer Clerk and fix cache
        console.log('⚠️ Role cache mismatch, preferring Clerk role and fixing cache:', { clerk: usertype, cached });
        try { localStorage.setItem('cb.usertype', usertype); } catch {}
      }
      
      // CB:BEGIN fallback-usertype-detection
      // If still no usertype found, try to determine from user context
      if (!usertype || usertype === '') {
        console.log('🔍 No usertype found, attempting fallback detection');
        
        // Check if user was just created (has recent timestamp)
        const justSignedUp = localStorage.getItem('just_signed_up');
        const justSignedUpTimestamp = localStorage.getItem('just_signed_up_timestamp');
        const isRecentSignup = justSignedUpTimestamp && 
                              (Date.now() - parseInt(justSignedUpTimestamp)) < 300000; // 5 minutes
        
        if (justSignedUp === 'true' && isRecentSignup) {
          console.log('🔍 Recent signup detected, assuming homeowner');
          usertype = 'homeowner';
          localStorage.setItem('cb.usertype', 'homeowner');
        }
      }
      // CB:END fallback-usertype-detection
      
      const contractorOnly = new Set(['contractor-dashboard','current-bids','contractor-project-details','contractor-project-milestones','contractor-verification','contractor-landing']);
      const realtorOnly = new Set(['realtor-dashboard','realtor-quote-detail']); // CB:BEGIN fr-014-realtor-only
      // CB:END fr-014-realtor-only
      const homeownerOnly = new Set(['customer-dashboard','documents','roof-report-coming-soon','payment-screen','reports','report','homeowner-report','contractor-bids']);
      
      // CB:BEGIN admin-routing-logic
      const adminOnly = new Set(['admin-dashboard','admin-homeowners-list','admin-contractors-list','admin-projects-list','admin-settings','admin-project-cockpit','admin-users','admin-users-edit','admin-projects','admin-project-detail','admin-homeowner-add','admin-contractor-add','admin-edit-homeowner-dashboard','admin-edit-contractor-dashboard','admin-quotes-new','admin-invoices-create','admin-revenue-kpi','admin-operations','admin-finance','admin-ops','admin-verification-queue','admin-documents']);
      
      // Handle admin routes
      if (adminOnly.has(target)) {
        console.log('🚦 App navigation check: Admin route detected', { target, usertype, meta });
        
        // CB:BEGIN api-admin-check
        // Check admin role via /api/me endpoint instead of metadata
        // CB:BEGIN fix-401-on-api-me-admin-check
        try {
          if (!isSignedIn || !getToken) return false;
          const token = await getToken();
          if (!token) return false;
          
          const response = await fetch(getApiUrl('/api/me'), {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            credentials: 'include'
          });
          // CB:END fix-401-on-api-me-admin-check
          if (response.ok) {
            const userData = await response.json();
            const apiRole = userData?.role;
            const apiSaysAdmin = apiRole === 'ADMIN';
            console.log('🔐 Admin check via API:', { role: apiRole, isAdmin: apiSaysAdmin });
            // If API conclusively says not admin, block. If API is inconclusive (no role), fall back to metadata.
            if (apiRole && !apiSaysAdmin) {
              toast.error('This area is for administrators only.');
              setCurrentScreen('landing');
              return;
            }
            if (apiSaysAdmin) {
            console.log('🚀 Setting currentScreen to:', target);
            setCurrentScreen(target);
            return;
            }
            // apiRole missing → fall through to metadata fallback below
          }
        } catch (error) {
          console.error('❌ Error checking admin role:', error);
        }
        
        // Fallback to metadata check
        const isAdmin = usertype === 'admin' || meta?.usertype === 'admin' || meta?.userType === 'admin';
        if (!isAdmin) {
          toast.error('This area is for administrators only.');
          setCurrentScreen('landing');
          return;
        }
        // CB:END api-admin-check
        
        // CB:BEGIN admin-nav-fix
        // Navigate to the requested admin screen when user is admin
        setCurrentScreen(target);
        return;
        // CB:END admin-nav-fix
      }
      // CB:END admin-routing-logic
      
      // FIX: Do not rewrite '/contractor/projects/:id' to legacy aliases.
      // If you still support 'project-details' as an internal screen id, it must carry an id.
      function normalizePath(input: string) {
        if (input.startsWith('/contractor/projects/')) return input; // preserve dynamic route
        if (input === 'project-details') {
          console.warn('normalize: project-details without id → redirecting to current-bids');
          return '/contractor/current-bids';
        }
        return input;
      }
      
      const normalizedTarget = normalizePath(target);
      
      // Debug logging (dev only)
      if (import.meta.env.DEV) console.log('🔍 Role gate debug:', { userId: user?.id, usertype, targetScreen: normalizedTarget });
      
      // Check contractor routes with enhanced UX feedback
      if (contractorOnly.has(normalizedTarget)) {
        if (import.meta.env.DEV) console.log('🚦 App navigation check:', { target, usertype, localStorage: localStorage.getItem("cb.usertype") });
        const { allow, demo } = gateContractorRoute(usertype as 'homeowner' | 'contractor' | 'unknown');
        if (!allow) {
          toast.error('This area is for contractors. Create a contractor profile to continue.');
          setCurrentScreen('contractor-landing');
          return;
        }
        if (demo) {
          sessionStorage.setItem('cb.demo.banner', '1');
        }
      }
      // CB:BEGIN fr-014-realtor-route-gate
      if (realtorOnly.has(normalizedTarget)) {
        if (usertype !== 'realtor') {
          toast.error('This area is for realtors. Sign in as a realtor to continue.');
          setCurrentScreen('realtor-landing');
          return;
        }
      }
      // CB:END fr-014-realtor-route-gate
      // Check homeowner routes
      if (homeownerOnly.has(normalizedTarget)) {
        // CB:BEGIN allow-next-steps-pre-auth
        // Allow /next-steps (roof-report-coming-soon) even if user isn't signed in yet
        // This is part of the signup flow and should be accessible pre-authentication
        if (normalizedTarget === 'roof-report-coming-soon') {
          if (window.location.pathname === '/next-steps') {
            // User is already on /next-steps URL - allow it
            setCurrentScreen('roof-report-coming-soon');
            return;
          }
          // User is trying to navigate TO next-steps during signup - allow it
          if (!isSignedIn || usertype === 'unknown') {
            console.log('✅ Allowing next-steps during signup flow');
            setCurrentScreen('roof-report-coming-soon');
            return;
          }
        }
        // CB:END allow-next-steps-pre-auth
        
        if (usertype !== 'homeowner') {
          console.log('🚫 Role gate: homeowner-only screen with non-homeowner cache; forcing customer-dashboard');
          setCurrentScreen('customer-dashboard');
          return;
        }
      }
    } catch (e) {
      console.warn('Role gate error:', e);
    }
    // CB:END unified-role-gate
    
    // Allow contractor-signup for everyone (no role restrictions)
    if (target === 'contractor-signup') {
      console.log('🚦 Allowing contractor signup for all users');
    }
    
    // CB:BEGIN allow-signup-for-everyone
    // Allow signup for everyone (no role restrictions, no auth required)
    if (target === 'signup') {
      console.log('🚦 Allowing signup for all users');
    }
    // CB:END allow-signup-for-everyone

    setCurrentScreen(target);
  };
  
  // URL synchronization hook
  const { navigateWithUrl } = useUrlSync(currentScreen, handleNavigate);

  const handleCopyrightClick = () => {
    // Copyright link acts as development portal entry point
    // - Disables demo mode (live user experience)
    // - Shows password prompt for additional security
    // - Dev mode is only enabled AFTER password is entered
    disableDemoMode();
    setShowPasswordPrompt(true);
  };

  const handlePasswordSubmit = (password: string) => {
    if (password === 'CoreBids0728$$') {
      setShowPasswordPrompt(false);
      setCurrentScreen('landing');
      // Ensure demo mode is disabled for development portal access
      disableDemoMode();
      return true;
    } else {
      // Password is incorrect - this will be handled by the password prompt component
      return false;
    }
  };

  const handlePasswordCancel = () => {
    setShowPasswordPrompt(false);
  };

  // Extract URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const pathParams = window.location.pathname.match(/\/admin\/homeowner-detail\/([^/]+)/);
  
  const renderScreen = () => {
    // CB:BEGIN admin-router-integration
    // DISABLED: React Router for admin paths - using screen-based system instead
    // if (window.location.pathname.startsWith('/admin')) {
    //   return (
    //     <BrowserRouter>
    //       <Routes>
    //         <Route path="/admin/*" element={<AdminLayout onNavigate={navigateWithUrl} />}>
    //           <Route index element={<AdminDashboard currentScreen="admin-dashboard" onNavigate={navigateWithUrl} />} />
    //           <Route path="users" element={<AdminUsersPage />} />
    //           <Route path="users/:id/edit" element={<AdminUserEditPage />} />
    //           <Route path="projects" element={<AdminProjectsPage />} />
    //           <Route path="projects/:id" element={<AdminProjectDetailPage />} />
    //           <Route path="homeowners-list" element={<AdminHomeownersList onNavigate={navigateWithUrl} />} />
    //           <Route path="homeowners/add" element={<AdminAddHomeowner onNavigate={navigateWithUrl} />} />
    //           <Route path="contractors-list" element={<AdminContractorsList onNavigate={navigateWithUrl} />} />
    //           <Route path="contractors/add" element={<AdminAddContractor onNavigate={navigateWithUrl} />} />
    //           <Route path="project-cockpit" element={<ProjectCockpit onNavigate={navigateWithUrl} />} />
    //           <Route path="settings" element={<AdminSettings onNavigate={navigateWithUrl} />} />
    //           <Route path="*" element={<Navigate to="/admin" replace />} />
    //         </Route>
    //         <Route path="*" element={<Navigate to="/admin" replace />} />
    //       </Routes>
    //     </BrowserRouter>
    //   );
    // }
    // CB:END admin-router-integration

    // Only use currentScreen-based routing for non-admin paths
    switch (currentScreen) {
      case 'sign-in': {
        // CB:BEGIN sign-in-modal-trigger
        // Show landing page behind and open Clerk modal on top — no full-page redirect
        return <SignInModalLauncher onNavigate={navigateWithUrl} openSignIn={openSignIn} />;
        // CB:END sign-in-modal-trigger
      }
      case 'landing':
        // CB:BEGIN landing-ui-parity
        // Use production LandingPage UI
        return <LandingPage onNavigate={navigateWithUrl} />;
        // CB:END landing-ui-parity
      case 'mobile-landing':
        return <MobileLanding onNavigate={setCurrentScreen} />;
      case 'project-wizard':
        return <ProjectWizard onNavigate={navigateWithUrl} />;
      case 'estimate-form':
        return <EstimateForm onNavigate={navigateWithUrl} />;
      case 'signup':
        return <TwilioOptInPage onNavigate={navigateWithUrl} />;
      case 'roof-report-coming-soon':
        return <NextSteps onNavigate={navigateWithUrl} />;
      case 'customer-dashboard':
        return <CustomerDashboard onNavigate={navigateWithUrl} />;

      // CB:BEGIN homeowner-help-screen
      case 'homeowner-help':
        return <HomeownerHelpPage onNavigate={navigateWithUrl} />;
      // CB:END homeowner-help-screen
      case 'documents':
        return <HomeownerDocumentsPage onNavigate={navigateWithUrl} />;

      case 'project-milestones':
        return (
          <Suspense fallback={<ScreenFallback />}>
            <ProjectMilestonePage onNavigate={navigateWithUrl} />
          </Suspense>
        );

      case 'escrow-dashboard':
        return (
          <Suspense fallback={<ScreenFallback />}>
            <EscrowDashboard onNavigate={navigateWithUrl} />
          </Suspense>
        );

      case 'contractor-escrow-dashboard':
        return (
          <Suspense fallback={<ScreenFallback />}>
            <ContractorEscrowDashboard onNavigate={navigateWithUrl} />
          </Suspense>
        );

      case 'enhancify-financing':
        return (
          <Suspense fallback={<ScreenFallback />}>
            <EnhancifyFinancing onNavigate={navigateWithUrl} />
          </Suspense>
        );

      case 'settings':
        return <Settings />;

      case 'contractor-bids':
        return (
          <ErrorBoundary>
            <ContractorBids onNavigate={navigateWithUrl} />
          </ErrorBoundary>
        );
      case 'contractor-details':
        return (
          <ErrorBoundary>
            <ContractorDetails onNavigate={navigateWithUrl} />
          </ErrorBoundary>
        );
      case 'payment-screen':
        return (
          <ErrorBoundary>
            <PaymentScreen onNavigate={navigateWithUrl} />
          </ErrorBoundary>
        );
      case 'contractor-dashboard':
        return (
          <ErrorBoundary>
            <ContractorDashboard onNavigate={navigateWithUrl} />
          </ErrorBoundary>
        );
      // CB:BEGIN contractor-help-screen
      case 'contractor-help':
        return <ContractorHelpPage onNavigate={navigateWithUrl} />;
      // CB:END contractor-help-screen
      case 'contractor-payments':
        return (
          <ErrorBoundary>
            <ContractorPayments onNavigate={navigateWithUrl} />
          </ErrorBoundary>
        );
      case 'current-bids':
        return <CurrentBids onNavigate={navigateWithUrl} />;
      // CB:BEGIN contractor-active-projects
      case 'contractor-active-projects':
        return (
          <ErrorBoundary>
            <ContractorActiveProjects onNavigate={navigateWithUrl} />
          </ErrorBoundary>
        );
      // CB:END contractor-active-projects
      // FIX: Dedicated project details route; must include :projectId param
      case 'contractor-project-details':
        return (
          <ErrorBoundary>
            <AuthRedirectWrapper onNavigate={navigateWithUrl}>
              <ProjectDetails onNavigate={navigateWithUrl} />
            </AuthRedirectWrapper>
          </ErrorBoundary>
        );
      case 'contractor-project-milestones':
        return (
          <ErrorBoundary>
            <AuthRedirectWrapper onNavigate={navigateWithUrl}>
              <Suspense fallback={<ScreenFallback />}>
                <ContractorMilestonePage onNavigate={navigateWithUrl} />
              </Suspense>
            </AuthRedirectWrapper>
          </ErrorBoundary>
        );
      case 'contractor-landing':
        return <ContractorLandingPage onNavigate={navigateWithUrl} />;
      // CB:BEGIN fr-014-realtor-screens
      case 'realtor-landing':
        return <RealtorLanding onNavigate={navigateWithUrl} />;
      case 'realtor-dashboard':
        return <RealtorDashboard onNavigate={navigateWithUrl} />;
      case 'realtor-quote-detail': {
        const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
        const match = pathname.match(/\/realtor\/quotes\/([^/]+)/);
        const quoteId = match?.[1] ?? '';
        return <RealtorQuoteDetail quoteId={quoteId} onNavigate={navigateWithUrl} />;
      }
      case 'realtor-client-portal': {
        const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
        const match = pathname.match(/\/realtor\/portal\/([^/]+)/);
        const token = match?.[1] ?? '';
        return <RealtorClientPortal token={token} onNavigate={navigateWithUrl} />;
      }
      // CB:END fr-014-realtor-screens
      // CB:BEGIN contractor-signup-screen
      case 'contractor-signup':
        return (
          <ErrorBoundary>
            <ContractorSignup onNavigate={navigateWithUrl} />
          </ErrorBoundary>
        );
      // CB:END contractor-signup-screen
      // CB:BEGIN invite-preview-screen
      case 'invite-preview':
        return (
          <ErrorBoundary>
            <InvitePreview onNavigate={navigateWithUrl} />
          </ErrorBoundary>
        );
      // CB:END invite-preview-screen
      case 'waitlist':
        return <Waitlist onCopyrightClick={handleCopyrightClick} onNavigate={navigateWithUrl} />;
      case 'serviced-zips':
        return <ServicedZips onNavigate={navigateWithUrl} />;
      case 'faq':
        return <FAQ onNavigate={navigateWithUrl} />;
      case 'accept-terms':
        return (
          <AcceptTermsPage
            onNavigate={navigateWithUrl}
            onAccepted={() => {
              refreshProfile();
            }}
          />
        );
      case 'terms':
        return <Terms onNavigate={navigateWithUrl} />;
      case 'payment-terms':
        return <PaymentProtectionTerms onNavigate={navigateWithUrl} />;
      case 'homeowner-agreement':
        return <HomeownerProjectAgreement onNavigate={navigateWithUrl} />;
      case 'contractor-agreement':
        return <ContractorBidAgreement onNavigate={navigateWithUrl} />;
      case 'about':
        return <AboutCoreBids onNavigate={navigateWithUrl} />;
      case 'contractor-verification':
        return (
          <ErrorBoundary>
            <ContractorOnboarding onNavigate={navigateWithUrl} />
          </ErrorBoundary>
        );
      case 'contractor-license-verification':
        return (
          <ErrorBoundary>
            <Suspense fallback={<ScreenFallback />}>
              <ContractorLicenseVerification onNavigate={navigateWithUrl} />
            </Suspense>
          </ErrorBoundary>
        );
      case 'contractor-insurance-upload':
        return (
          <ErrorBoundary>
            <Suspense fallback={<ScreenFallback />}>
              <ContractorInsuranceUpload onNavigate={navigateWithUrl} />
            </Suspense>
          </ErrorBoundary>
        );
      case 'not-found':
        return <NotFound />;
      case 'admin-tools':
        return <AdminTools />;
      // CB:BEGIN admin-dashboard-routing-protected
      case 'admin-dashboard':
        return (
          <AdminDashboard currentScreen={currentScreen} onNavigate={navigateWithUrl} />
        );
      case 'admin-project-cockpit':
        return (
          // CB:BEGIN admin-cockpit-route
          <ProjectCockpit onNavigate={navigateWithUrl} />
          // CB:END admin-cockpit-route
        );
      case 'admin-users':
        return <AdminUsersPage onNavigate={navigateWithUrl} />;
      case 'admin-users-edit':
        return <AdminUserEditPage />;
      case 'admin-projects':
        return <AdminProjectsPage />;
      case 'admin-project-detail':
        return <AdminProjectDetailPage />;
      // CB:END admin-dashboard-routing-protected
      // COREBIDS FIX: manual-bids homeowner flow screens
      case 'homeowner-dashboard':
        return <HomeownerDashboard projectId={urlParams.get('projectId') || ''} />;
      case 'admin-homeowners-list':
        return <AdminDashboard currentScreen={currentScreen} onNavigate={navigateWithUrl} />;
      case 'admin-homeowner-add':
        return <AdminDashboard currentScreen={currentScreen} onNavigate={navigateWithUrl} />;
      case 'admin-homeowner-detail':
        return <AdminDashboard currentScreen={currentScreen} onNavigate={navigateWithUrl} />;
      case 'admin-contractors-list':
        return <AdminDashboard currentScreen={currentScreen} onNavigate={navigateWithUrl} />;
      case 'admin-contractor-add':
        return <AdminDashboard currentScreen={currentScreen} onNavigate={navigateWithUrl} />;
      case 'admin-edit-homeowner-dashboard':
        return <AdminDashboard currentScreen={currentScreen} onNavigate={navigateWithUrl} />;
      case 'admin-edit-contractor-dashboard':
        return <AdminDashboard currentScreen={currentScreen} onNavigate={navigateWithUrl} />;
      case 'admin-contractor-detail':
        return <AdminDashboard currentScreen={currentScreen} onNavigate={navigateWithUrl} />;
      case 'admin-projects-list':
        return <AdminDashboard currentScreen={currentScreen} onNavigate={navigateWithUrl} />;
      case 'admin-settings':
        return <AdminDashboard currentScreen={currentScreen} onNavigate={navigateWithUrl} />;
      case 'admin-create':
        return <AdminCreate />;
      // CB:BEGIN fr-034-admin-realtors-route
      case 'admin-realtors':
        return <AdminRealtorsPage onNavigate={navigateWithUrl} />;
      // CB:END fr-034-admin-realtors-route
      // CB:BEGIN admin-reports-route
      case 'admin-reports':
        return <AdminReportsPage />;
      // CB:END admin-reports-route
      // CB:BEGIN admin-audit-logs-route
      case 'admin-audit-logs':
        return <AdminAuditLogsPage />;
      // CB:END admin-audit-logs-route
      case 'report':
        return <Report onNavigate={navigateWithUrl} />;
      case 'privacy-policy':
        return <PrivacyPolicy onNavigate={navigateWithUrl} />;
      case 'accessibility-statement':
        return <AccessibilityStatement onNavigate={navigateWithUrl} />;
      case 'reports':
        return <HomeownerReportPage onNavigate={navigateWithUrl} />;
      case 'data-tracking':
        return (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Loading KPI Tracker...</div>}>
            <KPITracker />
          </Suspense>
        );
      // CB:BEGIN eagleview-sandbox-route
      case 'eagleview-sandbox':
        return <EagleViewSandbox onNavigate={navigateWithUrl} />;
      // CB:END eagleview-sandbox-route
      // CB:BEGIN stripe-demo-screens
      case 'stripe-connect-demo':
        return <StripeConnectDemo />;
      case 'demo-payments':
        return <PaymentsEscrowDemo />;
      // CB:END stripe-demo-screens
      case 'welcome-email':
        return <div style={{ width: '100%', height: '100svh', overflow: 'auto', background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)' }}>
          <div style={{ maxWidth: '600px', margin: '0 auto', backgroundColor: '#ffffff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', marginTop: '40px', marginBottom: '40px' }}>
            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg, #0284c7, #0369a1)', textAlign: 'center', padding: '30px 20px', position: 'relative' }}>
              <h1 style={{ margin: '0', fontSize: '28px', color: '#ffffff', fontWeight: 'bold' }}>Welcome to CoreBids!</h1>
            </div>

            {/* Main content */}
            <div style={{ padding: '40px 30px', textAlign: 'left', background: '#ffffff' }}>
              <h3 style={{ marginTop: '0', fontSize: '24px', color: '#0284c7', marginBottom: '20px', fontWeight: '600' }}>Hi [First Name],</h3>
              
               <p style={{ lineHeight: '1.6', fontSize: '18px', margin: '0 0 15px 0', color: '#0284c7', fontWeight: '600' }}>3 roofing bids will be ready in 24hrs!</p>
               <p style={{ lineHeight: '1.6', fontSize: '16px', margin: '0 0 25px 0', color: '#374151' }}>Keep an eye on your inbox—we'll notify you as soon as they arrive.</p>
              
              <p style={{ lineHeight: '1.6', fontSize: '16px', margin: '0 0 25px 0', color: '#374151' }}>In the meantime, you can check your dashboard anytime for real-time status updates:</p>
              
              <div style={{ textAlign: 'center', margin: '30px 0' }}>
                <a href="[Dashboard Link]" style={{ display: 'inline-block', padding: '16px 32px', background: 'linear-gradient(135deg, #0284c7, #0369a1)', color: '#ffffff', textDecoration: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '18px', boxShadow: '0 4px 12px rgba(2, 132, 199, 0.3)' }}>
                  Check My Status
                </a>
              </div>

              <p style={{ margin: '30px 0 0 0', fontSize: '16px', color: '#475569', fontWeight: '500' }}>Best regards,</p>
              <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#64748b' }}>The CoreBids Team</p>
              <div style={{ textAlign: 'left', marginTop: '5px', marginBottom: '8px' }}>
                <img src="https://raw.githubusercontent.com/dan-corebids/CoreBids-Media/1e6de21b4640ee3caf75e72754aa18f5a9d49e49/CoreBids%20Logo%20(Combo).png" alt="CoreBids Logo" style={{ maxHeight: '30px' }} />
              </div>
            </div>

            {/* Footer (no link) */}
            <div style={{ textAlign: 'center', padding: '25px', fontSize: '13px', color: '#64748b', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
              © 2025 CoreBids. All rights reserved.
            </div>
          </div>
        </div>;
      case 'bids-in-email':
        return <div style={{ width: '100%', height: '100svh', overflow: 'auto', background: 'linear-gradient(135deg, #fefce8 0%, #fef3c7 100%)' }}>
          <div style={{ maxWidth: '600px', margin: '0 auto', backgroundColor: '#ffffff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', marginTop: '40px', marginBottom: '40px' }}>
             <div style={{ background: 'linear-gradient(135deg, #fbbf24, #f59e0b)', textAlign: 'center', padding: '30px 20px', position: 'relative' }}>
               <h1 style={{ margin: '0', fontSize: '28px', color: '#ffffff', fontWeight: 'bold', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>Your Bids are In! 🎉</h1>
             </div>
            <div style={{ padding: '40px 30px 0 30px', textAlign: 'left' }}>
              <h2 style={{ marginTop: '0', fontSize: '24px', color: '#f59e0b', marginBottom: '20px' }}>Hi [First Name],</h2>
               <p style={{ lineHeight: '1.6', fontSize: '18px', margin: '0 0 15px 0', color: '#f59e0b', fontWeight: '600' }}>Your project bids are in! 🎉</p>
               <p style={{ lineHeight: '1.6', fontSize: '16px', margin: '0 0 25px 0', color: '#374151' }}>Thank you for your patience! Proposals from licensed local contractors are ready now for you to choose the best fit for your home.</p>
              <div style={{ textAlign: 'center', margin: '30px 0' }}>
                <a href="[View My Bids Link]" style={{ display: 'inline-block', padding: '16px 32px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#ffffff', textDecoration: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '18px', boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)' }}>
                  View My Bids
                </a>
              </div>
               <p style={{ margin: '30px 0 0 0', fontSize: '16px', color: '#475569', fontWeight: '500' }}>Best regards,</p>
               <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#64748b' }}>The CoreBids Team</p>
               <div style={{ textAlign: 'left', marginTop: '5px', marginBottom: '30px' }}>
                 <img src="https://raw.githubusercontent.com/dan-corebids/CoreBids-Media/1e6de21b4640ee3caf75e72754aa18f5a9d49e49/CoreBids%20Logo%20(Combo).png" alt="CoreBids Logo" style={{ maxHeight: '30px' }} />
               </div>
            </div>
            <div style={{ textAlign: 'center', padding: '25px', fontSize: '13px', color: '#64748b', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
              © 2025 CoreBids. All rights reserved.
            </div>
          </div>
        </div>;
      case 'payment-confirmed-email':
        return <div style={{ width: '100%', height: '100svh', overflow: 'auto', background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)' }}>
          <div style={{ maxWidth: '600px', margin: '0 auto', backgroundColor: '#ffffff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', marginTop: '40px', marginBottom: '40px' }}>
            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg, #16a34a, #15803d)', textAlign: 'center', padding: '30px 20px', position: 'relative' }}>
              <h1 style={{ margin: '0', fontSize: '28px', color: '#ffffff', fontWeight: 'bold' }}>Demo Complete</h1>
            </div>

            {/* Main content */}
            <div style={{ padding: '40px 30px', textAlign: 'left', background: '#ffffff' }}>
              <h3 style={{ marginTop: '0', fontSize: '24px', color: '#16a34a', marginBottom: '20px', fontWeight: '600' }}>Hi [First Name],</h3>
              
              <p style={{ lineHeight: '1.6', fontSize: '16px', margin: '0 0 20px 0', color: '#374151' }}>Thank you for completing our demo — we really appreciate your time! 🙌</p>
              
              <p style={{ lineHeight: '1.6', fontSize: '16px', margin: '0 0 25px 0', color: '#374151' }}>We'd be extremely grateful if you could share your thoughts in a quick survey. Your feedback helps us improve CoreBids and ensure we're building something that truly makes your projects easier.</p>
              
              <div style={{ textAlign: 'center', margin: '30px 0' }}>
                <a href="[Survey Link]" style={{ display: 'inline-block', padding: '16px 32px', background: 'linear-gradient(135deg, #16a34a, #15803d)', color: '#ffffff', textDecoration: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '18px', boxShadow: '0 4px 12px rgba(22, 163, 74, 0.3)' }}>
                  Take the Survey
                </a>
              </div>

              <p style={{ margin: '30px 0 0 0', fontSize: '16px', color: '#475569', fontWeight: '500' }}>— The CoreBids Team</p>
              <div style={{ textAlign: 'left', marginTop: '5px', marginBottom: '8px' }}>
                <img src="https://raw.githubusercontent.com/dan-corebids/CoreBids-Media/1e6de21b4640ee3caf75e72754aa18f5a9d49e49/CoreBids%20Logo%20(Combo).png" alt="CoreBids Logo" style={{ maxHeight: '30px' }} />
              </div>
            </div>

            {/* Footer (no link) */}
            <div style={{ textAlign: 'center', padding: '25px', fontSize: '13px', color: '#64748b', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
              © 2025 CoreBids. All rights reserved.
            </div>
          </div>
        </div>;
      case 'email-report-ordered-preview':
        return <EmailReportOrdered />;
      case 'email-report-ready-preview':
        return <EmailReportReady />;
      case 'email-bid-accepted-preview':
        return <EmailBidAccepted />;
      case 'email-start-date-set-preview':
        return <EmailStartDateSet />;
      case 'email-start-reminder-preview':
        return <EmailStartReminder />;
      case 'email-out-of-scope-preview':
        return <EmailOutOfScope />;
      // CB:BEGIN clerk-diagnostics-route
      case '__clerk/diagnostics/ui':
        return <ClerkDiagnostics />;
      // CB:END clerk-diagnostics-route
      // CB:BEGIN admin-dynamic-routes
      default:
        // Handle all admin routes dynamically
        if (currentScreen?.startsWith('admin-')) {
          return (
            <AdminDashboard currentScreen={currentScreen} onNavigate={navigateWithUrl} />
          );
        }
        return <NotFound />;
      // CB:END admin-dynamic-routes
    }
  };

  // CB:BEGIN admin-direct-render-demo
  // Removed admin bypass - admin routes now go through normal flow
  // CB:END admin-direct-render-demo

  // CB:BEGIN exclude-financing-from-responsive-container
  // Enhancify financing widget needs full screen without any container padding
  const shouldWrapInContainer = currentScreen !== 'enhancify-financing';
  // CB:END exclude-financing-from-responsive-container
  // CB:BEGIN hide-dev-nav-on-help-screens
  const isHelpScreen = currentScreen === 'homeowner-help' || currentScreen === 'contractor-help';
  // CB:END hide-dev-nav-on-help-screens

  return (
    <div className="App">
      <AuthRedirectWrapper onNavigate={handleNavigate}>
        {shouldWrapInContainer ? (
          <ResponsiveContainer>
            {renderScreen()}
          </ResponsiveContainer>
        ) : (
          renderScreen()
        )}
      </AuthRedirectWrapper>
      {/* DevHud removed - build ID display not needed in production */}

      {/* Password Prompt Modal */}
      {showPasswordPrompt && (
        <PasswordPrompt
          onSubmit={handlePasswordSubmit}
          onCancel={handlePasswordCancel}
        />
      )}

      {/* Developer Navigation Pane */}
      {envShowDevPane && !isHelpScreen && (
        devNavCollapsed ? (
          <button
            style={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 2147483647,
              background: 'rgba(30,41,59,0.95)',
              borderRadius: 999,
              boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
              padding: 10,
              minWidth: 0,
              minHeight: 0,
              color: 'white',
              fontFamily: 'inherit',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
            }}
            aria-label="Expand Dev Navigation"
            onClick={() => setDevNavCollapsed(false)}
          >
            <span style={{ fontSize: 20, fontWeight: 700 }}>≡</span>
          </button>
        ) : (
          <div
            style={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 2147483647,
              background: 'rgba(30,41,59,0.95)',
              borderRadius: 12,
              boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
              padding: 8,
              minWidth: 103,
              maxWidth: 190,
              color: 'white',
              fontFamily: 'inherit',
              fontSize: 12,
              maxHeight: 390,
              overflowY: 'auto',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontWeight: 700, fontSize: 13, letterSpacing: 0.5 }}>Dev Nav</span>
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#dbeafe',
                  fontSize: 16,
                  cursor: 'pointer',
                  marginLeft: 8,
                  padding: 0,
                }}
                aria-label="Minimize Dev Navigation"
                onClick={() => setDevNavCollapsed(true)}
              >
                &minus;
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {[ 
                { key: 'landing', label: '1.a Landing' },
                { key: 'mobile-landing', label: '1.b Landing (Mobile)' },
                { key: 'project-wizard', label: '2. Project Wizard' },
                { key: 'estimate-form', label: '3. Estimate Form' },
                { key: 'roof-report-coming-soon', label: '4. Next Steps' },
                { key: 'customer-dashboard', label: '5. Customer Dashboard' },
                { key: 'contractor-bids', label: '6. Contractor Bids' },
                { key: 'payment-screen', label: '7. Payments Screen' },
                { key: 'reports', label: '8. Homeowner Reports' },
                { key: 'report', label: '9. Report (Mock)' },
                { key: 'contractor-landing', label: '10. Contractor Landing' },
                { key: 'contractor-signup', label: '11. Contractor SignUp' },
                { key: 'contractor-dashboard', label: '12. Contractor Dashboard' },
                { key: 'current-bids', label: '13. Current Bids' },
                { key: 'contractor-project-details', label: '14. Project Details' },
                { key: 'contractor-verification', label: '15. Contractor Verification' },
                // CB:BEGIN fr-014-dev-nav-realtor
                { key: 'realtor-landing', label: '15a. Realtor Landing' },
                { key: 'realtor-dashboard', label: '15b. Realtor Dashboard' },
                // CB:END fr-014-dev-nav-realtor
                { key: 'waitlist', label: '16. Waitlist' },
                { key: 'admin-tools', label: '17. Admin Dashboard' },
                { key: 'faq', label: '18. FAQ' },
                { key: 'terms', label: '19. Terms of Service' },
                { key: 'privacy-policy', label: '20. Privacy Policy' },
                { key: 'accessibility-statement', label: '21. Accessibility Statement' },
                { key: 'not-found', label: '22. Error 404' },
                { key: 'data-tracking', label: '101. Data Tracking' },
                { key: 'welcome-email', label: '102. Welcome Email' },
                { key: 'bids-in-email', label: '103. Your Bids are In!' },
                { key: 'payment-confirmed-email', label: '104. Payment Confirmed' },
                { key: 'email-report-ordered-preview', label: '201. Email • Report Ordered' },
                { key: 'email-report-ready-preview', label: '202. Email • Report Ready' },
                { key: 'email-bid-accepted-preview', label: '203. Email • Bid Accepted' },
                { key: 'email-start-date-set-preview', label: '204. Email • Start Date Set' },
                { key: 'email-start-reminder-preview', label: '205. Email • Start Reminder' },
                { key: 'email-out-of-scope-preview', label: '206. Email • Out of Scope' },
                // CB:BEGIN eagleview-sandbox-devnav
                { key: 'eagleview-sandbox', label: '105. EagleView Sandbox' },
                // CB:END eagleview-sandbox-devnav
                // CB:BEGIN stripe-demo-devnav
                { key: 'stripe-connect-demo', label: '106. Stripe Connect Demo' },
                { key: 'demo-payments', label: '107. Demo Payments' },
                // CB:END stripe-demo-devnav
              // CB:BEGIN add-admin-dashboard-devnav
              ].concat([
                { key: 'admin-dashboard', label: '17. Admin Dashboard' },
              ]).map((screen) => (
                <button
                  key={screen.key}
                  style={{
                    background: currentScreen === screen.key ? '#2563eb' : 'rgba(255,255,255,0.08)',
                    color: currentScreen === screen.key ? 'white' : '#dbeafe',
                    border: 'none',
                    borderRadius: 6,
                    padding: '4px 0',
                    fontWeight: 500,
                    fontSize: 12,
                    marginBottom: 1,
                    cursor: 'pointer',
                    transition: 'background 0.18s',
                  }}
                  onClick={() => setCurrentScreen(screen.key)}
                >
                  {screen.label}
                </button>
              ))}
              // CB:END add-admin-dashboard-devnav
            </div>
          </div>
        )
      )}
      
    </div>
  );
}

// CB:BEGIN meta-pixel-tracker-component
// Component to track Meta Pixel PageView on route changes
const MetaPixelTracker = () => {
  useMetaPixel();
  useAnalytics();
  return null;
};
// CB:END meta-pixel-tracker-component

// CB:BEGIN project-milestone-route-wrapper
// Wrapper to provide onNavigate to ProjectMilestonePage when accessed via React Router
const ProjectMilestonePageWrapper = () => {
  const navigate = useNavigate();
  const navigateWithScreen = (screen: string) => {
    // Convert screen names to URLs or use React Router navigation
    if (screen === 'customer-dashboard') {
      navigate('/');
      // Also trigger App's screen change if needed
      window.dispatchEvent(new CustomEvent('navigate', { detail: { screen } }));
    } else {
      navigate('/');
      window.dispatchEvent(new CustomEvent('navigate', { detail: { screen } }));
    }
  };
  
  return (
    <ErrorBoundary>
      <ProjectMilestonePage onNavigate={navigateWithScreen} />
    </ErrorBoundary>
  );
};
// CB:END project-milestone-route-wrapper

// CB:BEGIN react-router-v7-flags
const routerFuture = { v7_startTransition: true, v7_relativeSplatPath: true };
// CB:END react-router-v7-flags
const AppWrapper = () => {
  const queryClient = new QueryClient();
  return (
    <BrowserRouter future={routerFuture}>
      <QueryClientProvider client={queryClient}>
        <MobileProvider>
          <ProfileProvider>
            <MetaPixelTracker />
            <Suspense fallback={<ScreenFallback />}>
              <Routes>
                <Route path="/projects/:projectId" element={<HomeownerProjectPage />} />
                <Route path="/projects/:projectId/milestones" element={<ProjectMilestonePageWrapper />} />
                <Route path="/*" element={<App />} />
              </Routes>
            </Suspense>
          </ProfileProvider>
        </MobileProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
};

export default AppWrapper;
